import Loading from '../components/Loading';
import '../scss/view/prescription.scss';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import instance from '../config/axios';
import { useAuth } from 'react-oidc-context';
import { ColumnData, ROLE } from '../interface/interface';
import { Table } from '../components/Table';
import { PaginationState } from '@tanstack/react-table';
import qs from 'qs';
import Select, {
  DropdownIndicatorProps,
  SingleValue,
  components,
} from 'react-select';
import { activeOrInActiveList } from '../constant/constant';
import {
  DownCaretIcon,
  OrderSummaryIcon,
  UserHeartIcon,
  EditPatientIcon,
  TokenIcon,
} from '../assets/svgImages';
import moment from 'moment';
import CustomModal from '../components/CustomModal';
import showToast from '../components/ToastComponent';

interface Patient {
  id: number;
  token: string;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    dateOfBirth: string;
    email: string;
    active: boolean;
  };
}

const Patients = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auth = useAuth();

  const [patients, setPatients] = useState<Patient[] | []>([]);
  const [isLoading, setLoadingStatus] = useState<boolean>(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [isSearchSubmitted, setSearchSubmissionStaus] = useState<number>(0);
  const [filter, setFilter] = useState<
    SingleValue<{
      label: string;
      value: string;
    }>
  >({ label: 'Active', value: 'active' });

  const [paginationCount, setTotalPaginationCount] = useState<number>(0);
  const [submitCount, setSubmitCount] = useState<number>(0);

  const [openTokenModal, setOpenTokenModal] = useState<boolean>(false);

  const [erxConfirmationModal, setErxConfirmationModal] =
    useState<boolean>(false);

  const [token, setToken] = useState<string>();

  const proceedToCreatePatient: boolean = true;

  const submitToken = async () => {
    await instance
      .post(
        '/api/v1/erxToken/addToken',
        {
          token,
          proceedToCreatePatient,
        },
        {
          headers: {
            Authorization: `Bearer ${user?.access_token}`,
          },
        },
      )
      .then((res) => {
        if (res.status === 200) {
          const message =
            res.data.message ?? 'Token added to patient successfully';
          showToast({
            message: message,
            type: 'success',
          });
          // proceedToCreatePatient = false;
          setOpenTokenModal(false);
        } else if (res.status === 405) {
          // setErxConfirmationModal(true);
          showToast({
            message: 'Patient Not found',
            type: 'warning',
          });
        } else {
          showToast({
            message: res.data.message,
            type: 'warning',
          });
        }
      })
      .catch((err) => {
        console.error(err);
        showToast({
          message: 'Failed to add token to Patient. Please try again later',
          type: 'error',
        });
        setOpenTokenModal(false);
      });
  };

  const columns: Array<ColumnData> = [
    {
      key: 'firstName',
      header: () => 'First Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'lastName',
      header: () => 'Last Name',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'dateOfBirth',
      header: () => 'Date of Birth',
      isSortable: true,
      cell: (info) => {
        const date = moment(info.getValue()).utc();
        return date.isValid()
          ? moment.utc(info.getValue()).format('DD/MM/YYYY')
          : '-';
      },
    },
    {
      key: 'email',
      header: () => 'Email',
      isSortable: true,
      cell: (info) => info.getValue(),
    },
    {
      key: 'status',
      header: () => 'Status',
      cell: (info) => (
        <span
          className={`px-5 py-1 rounded-full uppercase text-xs text-white 
          font-semibold ${info.getValue() ? 'bg-secondary-green-500' : 'bg-primary-red-600'}`}
        >
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'impersonate',
      header: () => 'Impersonate',
      cell: (info) => (
        <button
          type="button"
          className="ml-6"
          onClick={async () => {
            setLoadingStatus(true);
            instance({
              method: 'GET',
              url: `/api/v1/user/impersonate/${info.getValue().patientId}`,
              headers: {
                Authorization: `Bearer ${user?.access_token}`,
              },
              withCredentials: true,
            })
              .then((resp) => {
                console.log('D> impersonate response: ', resp);
                if (resp.data?.impersonateJwt) {
                  localStorage.setItem(
                    'impersonateUser',
                    JSON.stringify({ access_token: resp.data?.impersonateJwt }),
                  );
                  // dispatch?
                  auth.removeUser();
                  setLoadingStatus(false);
                  navigate('/home');
                }
              })
              .catch((e: unknown) => {
                setLoadingStatus(false);
                console.error('D> error fetching patients: ', e);
              });
          }}
        >
          <EditPatientIcon />
        </button>
      ),
    },
    {
      key: 'edit',
      header: () => 'View Profile',
      cell: (info) => (
        <button
          type="button"
          className="ml-6"
          onClick={() => {
            if ((user?.profile.groups as string[]).includes(ROLE.Doctors)) {
              navigate(`/patient/${info.getValue().patientId}/orders`);
              return;
            }
            navigate(`/patient/${info.getValue().patientId}`);
          }}
        >
          <OrderSummaryIcon />
        </button>
      ),
    },
  ];

  const formateDate = (value: string) => {
    const date = moment(value, 'DD/MM/YYYY', true);
    if (date) return date.format('YYYY-MM-DD');
    return value;
  };

  useEffect(() => {
    setLoadingStatus(true);
    const dateRegex2 = /^\d{2}\/\d{2}\/\d{4}$/;
    instance({
      method: 'GET',
      url: `/api/v1/patients?${qs.stringify({
        page: pagination.pageIndex,
        limit: pagination.pageSize,
        search: dateRegex2.test(search) ? formateDate(search) : search,
        filter: [filter?.value],
      })}`,
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
    })
      .then((resp) => {
        setPatients(() => [
          ...resp.data.data.map((d: any) => ({
            ...d,
            status: d?.isActive,
            impersonate: { patientId: d?.patientId },
            edit: { patientId: d?.patientId },
          })),
        ]);
        setTotalRecords(resp?.data?.total);
        setLoadingStatus(false);

        if (
          paginationCount !==
            Math.ceil(resp.data.total / pagination.pageSize) &&
          submitCount < isSearchSubmitted
        ) {
          setTotalPaginationCount(
            Math.ceil(resp.data.total / pagination.pageSize),
          );
          setSubmitCount(isSearchSubmitted);
          setPagination({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }
      })
      .catch((e: unknown) => {
        console.error('D> error fetching patients: ', e);
        setLoadingStatus(false);
      });
  }, [pagination, isSearchSubmitted, filter]);

  if (isLoading) {
    return <Loading />;
  }
  const DropdownIndicator = (props: DropdownIndicatorProps<true>) => {
    return (
      <components.DropdownIndicator {...props}>
        <DownCaretIcon />
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      {(user?.profile.groups as string[]).includes(ROLE.Admin) ||
      (user?.profile.groups as string[]).includes(ROLE.PLO) ||
      (user?.profile.groups as string[]).includes(ROLE.Dispense_Tech) ? (
        <>
          <Link className="absolute top-0 right-0 mt-8" to={'/new-patient'}>
            <button
              type="button"
              className="ml-2 rounded-md bg-secondary-pink-600 px-10 py-3 text-center text-sm font-semibold flex text-white items-center"
            >
              <UserHeartIcon />
              <span className="ml-3">Add New Patient</span>
            </button>
          </Link>
          <button
            className="absolute top-0 right-[250px] mt-8 bg-primary-red-100 rounded-md text-primary-blue-500 text-sm font-semibold px-8 py-3 ml-auto flex"
            onClick={() => {
              setToken(undefined);
              setOpenTokenModal(true);
            }}
          >
            <TokenIcon height={17} />
            <span className="ml-3">Add Token</span>
          </button>
        </>
      ) : null}
      <Select
        className="w-48 patient-status-filter cursor-pointer"
        value={filter as any}
        options={activeOrInActiveList as any}
        onChange={(selected) => setFilter(selected as any)}
        isSearchable={false}
        components={{
          DropdownIndicator,
          IndicatorSeparator: () => null,
        }}
      />
      <Table
        columns={
          (user?.profile.groups as string[]).includes(ROLE.Doctors)
            ? columns.filter((x) => x.key !== 'impersonate')
            : columns
        }
        data={patients}
        enablePagination={true}
        enableSorting={true}
        enableSearch={true}
        pagintation={{ state: pagination, setter: setPagination, totalRecords }}
        search={{
          value: search,
          changeHandler: setSearch,
          submitSearch: setSearchSubmissionStaus,
        }}
      />
      <CustomModal
        isOpen={openTokenModal}
        onClose={() => setOpenTokenModal(false)}
        header={'Add Token to Patient'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={async () => await submitToken()}
            >
              Submit
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => setOpenTokenModal(false)}
            >
              Cancel
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        <p>Token</p>
        <div className="mt-2">
          <input
            type="text"
            name="reason"
            id="reason"
            autoComplete="given-name"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300
            placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>
      </CustomModal>

      <CustomModal
        isOpen={erxConfirmationModal}
        onClose={() => {
          // proceedToCreatePatient = false;
          setErxConfirmationModal(false);
          setOpenTokenModal(false);
        }}
        header={'Patient Not Found'}
        footer={
          <>
            <button
              className="mr-4 bg-primary-blue-500 text-white px-4 py-2 rounded"
              onClick={async () => {
                // proceedToCreatePatient = true;
                await submitToken();
                setErxConfirmationModal(false);
                setOpenTokenModal(false);
              }}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 px-4 py-2 rounded"
              onClick={() => {
                // proceedToCreatePatient = false;
                setErxConfirmationModal(false);
                setOpenTokenModal(false);
              }}
            >
              No
            </button>
          </>
        }
        modalSize={'w-3/12'}
      >
        <p>Do you want to create a Patient record?</p>
      </CustomModal>
    </>
  );
};

export default Patients;
